import React, { useMemo } from 'react';
import { GenericChartLoading } from 'src/components/common/Loading/PlaceHolderLoading/PlaceHolderLoading';
import { useAppSelector } from 'src/utils/Hooks';
import { withBus } from 'react-bus';
import Visualization from 'src/components/Layout/Advertising/ConnectPageLayout/components/Visualization';
import { DATE_FORMAT } from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/AudiencesTab/BrandedSearchRate/constants';
import BrandedSearchRateLineChart from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/AudiencesTab/BrandedSearchRate/BrandedSearchRateLineChart';
import {
  brandedSearchRateOverTimeAdvancedSearchParser,
  buildRequestForBrandedSearchRateOverTime
} from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/AudiencesTab/BrandedSearchRate/utils';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import useAmcDateRange from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/hooks/useAmcDateRange';
import _get from 'lodash/get';
import moment from 'moment';

const BrandedSearchRateVisualization = () => {
  const { startDayId, endDayId } = useAmcDateRange();
  const { retailer, mainTimePeriod } = useAppSelector((state) => state);
  const { startDayId: mainPeriodStartDay, endDayId: mainPeriodEndDay } = mainTimePeriod;

  // Advanced Search Request Body
  const brandedSearchRateOverTimeRequestBody = useMemo(
    () => buildRequestForBrandedSearchRateOverTime({ startDayId, endDayId, retailerId: retailer.id }),
    [endDayId, startDayId, retailer.id]
  );

  // Fetching data
  const { data, isLoading } = useGenericAdvancedSearch({
    requestBody: brandedSearchRateOverTimeRequestBody,
    queryKeys: brandedSearchRateOverTimeRequestBody,
    requestId: 'branded-search-rate-over-time'
  });

  // Data Access
  const dataForChart = useMemo(() => {
    if (data) {
      const documentData = brandedSearchRateOverTimeAdvancedSearchParser(_get(data, 'data[0]'));
      // Get the key to pull metric values from our filtered documents based on the first dropdown option selected
      const formattedMinDate = String(mainPeriodStartDay).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'); // 20230417 --> 2023-04-17
      const formattedMaxDate = String(mainPeriodEndDay).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
      const minEpochTimeStamp = moment.utc(formattedMinDate, DATE_FORMAT).valueOf();
      const maxEpochTimeStamp = moment.utc(formattedMaxDate, DATE_FORMAT).valueOf();

      // Filter our results based on Group By option selected (EX: All Ad Types or just Search Ads).
      // We also filter out documents that are being returned outside of our selected start and end date ranges.
      const filteredDocuments = documentData.filter((document) => {
        const eventDate = moment.utc(document.additionalMetaData['impressionDate.keyword']).valueOf();
        const isWithinDateRange: boolean = eventDate >= minEpochTimeStamp && eventDate <= maxEpochTimeStamp;
        return isWithinDateRange;
      });
      // Create total numerator/denominator sums for the legend value calculation
      let totalNumeratorSum = 0;
      let totalDenominatorSum = 0;

      // Create a map for each unique timestamp to group our values
      const valuesByUniqueEpochDate = new Map();

      // Using our filtered document collection, access the numerator value and denominator values depending on the metric we have selected.
      filteredDocuments.forEach((document) => {
        const numeratorValue = document.additionalValues.numberOfBrandedSearches_sum_value;
        const denominatorValue = document.additionalValues.impressions_sum_value;

        // Maintain total numerator sum and denominator sum for the legend value
        totalNumeratorSum += numeratorValue;
        totalDenominatorSum += denominatorValue;

        // If we haven't seen the date yet, add a new item to our map and set the sums to an initial value
        if (!valuesByUniqueEpochDate.has(document.additionalMetaData['impressionDate.keyword'])) {
          valuesByUniqueEpochDate.set(document.additionalMetaData['impressionDate.keyword'], {
            numeratorSum: numeratorValue,
            denominatorSum: denominatorValue
          });
        } else {
          // Otherwise, pull the item out of the map and add to the sums
          const doc = valuesByUniqueEpochDate.get(document.additionalMetaData['impressionDate.keyword']);
          doc.numeratorSum += numeratorValue;
          doc.denominatorSum += denominatorValue;
        }
      });

      // The chart expects a collection of tuples or sub-arrays formatted as [[timestamp, value]]
      const tuples = [];

      // For each date in the map, calculate the ratios and add to our collection
      valuesByUniqueEpochDate.forEach((item, date) => {
        // Avoid division by 0 errors
        const ratio = item.denominatorSum !== 0 ? (item.numeratorSum / item.denominatorSum) * 100 : 0;
        const epochDate = moment.utc(date, DATE_FORMAT).valueOf();
        tuples.push([epochDate, ratio]);
      });

      // Calculate legend value
      const legendValue = totalDenominatorSum !== 0 ? totalNumeratorSum / totalDenominatorSum : 0;

      // Sort by epoch date to avoid Highcharts issues
      tuples.sort((a, b) => a[0] - b[0]);
      return { chartData: tuples, legendValue };
    }
    return null;
  }, [data]);

  return (
    <Visualization
      titleProps={{
        title: ''
      }}
    >
      <div style={{ marginTop: '24px' }}>
        {isLoading && <GenericChartLoading />}
        {!isLoading && dataForChart && (
          <BrandedSearchRateLineChart data={dataForChart.chartData} legendValue={dataForChart.legendValue} />
        )}
      </div>
    </Visualization>
  );
};

export default withBus('eventBus')(BrandedSearchRateVisualization);
